import { useState, useEffect, useRef } from "react";
import { authenticatedFetch } from "../utils/api";

interface AdminMessageModalProps {
  playerId: string;
  playerName: string;
  teamName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AdminMessageModal({
  playerId,
  playerName,
  teamName,
  isOpen,
  onClose,
  onSuccess
}: AdminMessageModalProps) {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      setError("Message cannot be empty");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await authenticatedFetch("/messages/admin-send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          player_id: playerId,
          message: message.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send message");
      }

      // Reset form
      setMessage("");
      setError("");
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage("");
      setError("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "1.5rem",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 1rem 0", color: "#212529" }}>
          Send Admin Message to {playerName}
        </h3>
        <p style={{ margin: "0 0 1rem 0", color: "#6c757d", fontSize: "0.875rem" }}>
          Team: {teamName}
        </p>

        {error && (
          <div
            style={{
              padding: "0.75rem",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
              marginBottom: "1rem",
              fontSize: "0.875rem",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label
              htmlFor="admin-message"
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "bold",
                color: "#212529",
              }}
            >
              Message (no limits)
            </label>
            <textarea
              ref={textareaRef}
              id="admin-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              rows={8}
              disabled={isSubmitting}
              style={{
                width: "100%",
                padding: "0.75rem",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                resize: "vertical",
                minHeight: "150px",
                boxSizing: "border-box",
              }}
            />
            <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "#6c757d" }}>
              {message.length} characters
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !message.trim()}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                cursor: isSubmitting || !message.trim() ? "not-allowed" : "pointer",
                backgroundColor: isSubmitting || !message.trim() ? "#6c757d" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: isSubmitting || !message.trim() ? 0.6 : 1,
              }}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

